import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { combineLatest, Observable } from 'rxjs';
import { IPatient } from 'src/app/models/patient.model';
import { IAnamneseDetail } from 'src/app/models/anamnese.model';
import { AnamneseSectionDto } from 'src/app/models/anamnese-section.model';
import { IAnamneseQuestion, AnamneseQuestionOptionDto } from 'src/app/models/anamnese-form-question.model';
import { AnamneseService } from 'src/app/services/anamnese.service';
import { ModulesService } from 'src/app/services/modules.service';
import { setWarningText } from 'src/app/reducers/warning-popup.reducer';
import { IError } from 'src/app/models/error.model';
import { 
  faChevronLeft, 
  faPrint, 
  faFileAlt, 
  faWarning,
  faCheck,
  faClock
} from '@fortawesome/free-solid-svg-icons';

@Component({
  selector: 'app-anamnese-view-v2',
  templateUrl: './anamnese-view.component.html',
  styleUrls: ['./anamnese-view.component.sass']
})
export class AnamneseViewV2Component implements OnInit {
  loading = true;
  anamneseId: string | null = '';
  patientId: string | null = '';
  
  anamnese: IAnamneseDetail | null = null;
  sections: AnamneseSectionDto[] = [];
  patient: IPatient | null = null;
  patientAge: number | null = null;
  isMinor = false;

  // Icons
  faChevronLeft = faChevronLeft;
  faPrint = faPrint;
  faFileAlt = faFileAlt;
  faWarning = faWarning;
  faCheck = faCheck;
  faClock = faClock;

  // Módulo dinâmico
  viewModules$: Observable<{ moduleName: string; moduleNamePascalCase: string; moduleNameInSingular: string; }>;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private anamneseService: AnamneseService,
    private store: Store<{ warningPopup: IError }>,
    public modulesService: ModulesService
  ) {
    this.viewModules$ = combineLatest({
      moduleName: this.modulesService.moduleName$,
      moduleNamePascalCase: this.modulesService.moduleNamePascalCase$,
      moduleNameInSingular: this.modulesService.moduleNameInSingular$
    });
  }

  ngOnInit(): void {
    this.route.paramMap.subscribe(params => {
      this.anamneseId = params.get('anamneseId');
      if (this.anamneseId) {
        this.loadAnamnese();
      } else {
        this.router.navigate(['main', 'v2', 'patients']);
      }
    });
  }

  loadAnamnese(): void {
    this.loading = true;

    Promise.all([
      this.anamneseService.getAnamneseById(this.anamneseId || ''),
      this.anamneseService.getQuestions(this.anamneseId || '')
    ]).then(([anamneseDetail, sections]) => {
      this.anamnese = anamneseDetail;
      this.patient = anamneseDetail.patient;
      this.patientId = anamneseDetail.patient_id || anamneseDetail.patient?.id || '';
      this.sections = sections;

      // Calcula idade do paciente
      if (this.patient?.birth_date) {
        this.patientAge = this.calculateAge(this.patient.birth_date);
        this.isMinor = this.patientAge !== null && this.patientAge < 18;
      }

      // Mapeia as respostas para as perguntas
      if (anamneseDetail.answers && anamneseDetail.answers.length > 0) {
        this.sections.forEach(section => {
          section.questions.forEach(question => {
            const answer = anamneseDetail.answers?.find(a => a.question_id === question.id);
            if (answer) {
              question.answer = answer.answer;
              question.observation = answer.observation;
            }
          });
        });
      }

      this.loading = false;
    }).catch((error: any) => {
      console.error('Erro ao carregar anamnese:', error);
      this.store.dispatch(setWarningText('Erro ao carregar ficha de anamnese.'));
      this.loading = false;
      this.router.navigate(['main', 'v2', 'patients']);
    });
  }

  calculateAge(birthDateString: string): number | null {
    if (!birthDateString) {
      return null;
    }

    let birthDate: Date;
    // Tentar normalizar a data, comum vir como AAAA-MM-DD ou DD/MM/AAAA
    if (birthDateString.includes('-')) { // Formato AAAA-MM-DD
      const parts = birthDateString.split('-');
      birthDate = new Date(Number(parts[0]), Number(parts[1]) - 1, Number(parts[2]));
    } else if (birthDateString.includes('/')) { // Formato DD/MM/AAAA
      const parts = birthDateString.split('/');
      birthDate = new Date(Number(parts[2]), Number(parts[1]) - 1, Number(parts[0]));
    } else {
      // Formato não reconhecido ou inválido
      console.error('Formato de data de nascimento não reconhecido:', birthDateString);
      return null;
    }

    if (isNaN(birthDate.getTime())) {
      console.error('Data de nascimento inválida após parse:', birthDateString);
      return null;
    }

    const today = new Date();
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }
    return age < 0 ? 0 : age; // Evitar idade negativa se data for futura
  }

  getAnswerDisplay(question: IAnamneseQuestion): string {
    if (question.answer === undefined || question.answer === null || question.answer === '') {
      return 'Não respondido';
    }

    const val = question.answer;

    switch (question.type) {
      case 'YES_NO':
        if (val === true || val === 'true' || val === 'Sim' || val === 'sim') return 'Sim';
        if (val === false || val === 'false' || val === 'Não' || val === 'não' || val === 'Nao' || val === 'nao') return 'Não';
        return 'Não respondido';

      case 'SINGLE_CHOICE':
        if (question.options) {
          const option = question.options.find(o => o.value === question.answer);
          return option?.label || question.answer;
        }
        return question.answer;

      case 'MULTIPLE_CHOICE':
        // A resposta pode vir como array ou como string JSON (ex: '["valor1","valor2"]')
        let answerArray: string[] = [];
        if (Array.isArray(question.answer)) {
          answerArray = question.answer;
        } else if (typeof question.answer === 'string') {
          try {
            const parsed = JSON.parse(question.answer);
            if (Array.isArray(parsed)) {
              answerArray = parsed;
            }
          } catch {
            // Se não for um JSON válido, trata como resposta simples
            answerArray = [question.answer];
          }
        }
        
        if (answerArray.length > 0 && question.options) {
          const labels = answerArray
            .map(val => question.options?.find(o => o.value === val)?.label || val)
            .filter(l => l);
          return labels.join(', ') || 'Não respondido';
        }
        return 'Não respondido';

      case 'TEXT_INPUT':
      default:
        return question.answer?.toString() || 'Não respondido';
    }
  }

  // ========== ACTIONS ==========
  goBack(): void {
    if (this.patientId) {
      this.router.navigate(['main', 'v2', 'patients', this.patientId, 'edit']);
    } else {
      this.router.navigate(['main', 'v2', 'patients']);
    }
  }

  printAnamnese(): void {
    window.print();
  }

  generateDocs(): void {
    if (this.anamneseId) {
      const isModelPatient = this.patient?.is_model_patient || false;
      this.router.navigate(['main', 'v2', 'patients', 'generate-docs', this.anamneseId], {
        queryParams: { patientId: this.patientId, isModelPatient: isModelPatient }
      });
    }
  }

  formatDate(dateString: string | undefined): string {
    if (!dateString) return '-';
    
    // Se já está no formato DD/MM/YYYY, retorna diretamente
    if (dateString.includes('/') && dateString.split('/').length === 3) {
      return dateString;
    }
    
    // Tenta fazer parse de formato ISO (YYYY-MM-DD)
    let date: Date;
    if (dateString.includes('-')) {
      const parts = dateString.split('-');
      date = new Date(Number(parts[0]), Number(parts[1]) - 1, Number(parts[2]));
    } else {
      date = new Date(dateString);
    }
    
    if (isNaN(date.getTime())) return '-';
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  }

  formatDateTime(dateString: string | undefined): string {
    if (!dateString) return '-';
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return '-';
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    return `${day}/${month}/${year} às ${hours}:${minutes}`;
  }
}
